import { Card, ConfirmationModal, ErrorAlert, Space } from '@procurify/ui'

import { useProcurifyIntl } from '@webapp/hooks'
import { ReportAccessGrantListPresenter } from '@webapp/platform/iam/features/list-report-access-grants/presenters/ReportAccessGrantListPresenter'
import { ReportAccessSecondaryCategoryListPresenter } from '@webapp/platform/iam/features/list-report-access-secondary-categories/presenters/ReportAccessSecondaryCategoryListPresenter'
import { ReportAccessActionRow } from './components/ReportAccessActionRow'
import { ReportAccessDetailEmptyState } from './components/ReportAccessDetailEmptyState'
import { ReportAccessDetailHeader } from './components/ReportAccessDetailHeader'
import { ReportAccessHeaderCard } from './components/ReportAccessHeaderCard'
import { ReportAccessSectionLoader } from './components/ReportAccessSectionLoader'
import { ReportAccessDetailPaneStates } from './enums'
import messages from './messages'
import { useReportAccessView } from './useReportAccessView'

/**
 * Feature Set: Report Access View (Page-level)
 *
 * Single master/detail surface orchestrating nine features: the
 * PrimaryCategory bootstrap + selector, the SecondaryCategory master list,
 * the grants tag list, and six grant/revoke commands dispatched from one
 * picker by mode. Owns all mode state and both danger confirmations.
 */
export const ReportAccessView = () => {
  const intl = useProcurifyIntl()

  const {
    states: {
      selectedPrimaryCategory,
      isAllPrimaryCategories,
      selectedSecondaryCategory,
      isAllSecondaryCategories,
      secondaryCategoryPage,
      submitAction,
      isRemoveAction,
      highlightedGrantId,
      isClearAllConfirmOpen,
      isBulkRemoveConfirmOpen,
      pendingClearAllCount,
      pendingBulkRemoveUser,
      detailMode,
      detailPaneState,
      detailPaneWidth,
      showSecondaryCategoryPane,
      grantListSecondaryCategoryId,
      isSubmitting,
      isSubmitDisabled,
      isGrantCommandPending,
      userPickerFocusRequest,
    },
    commands: {
      onSubmitUser,
      onRemoveGrant,
      onClearAllConfirm,
      onBulkRemoveConfirm,
    },
    methods: {
      onSelectPrimaryCategory,
      onSelectAllPrimaryCategories,
      onSelectSecondaryCategory,
      onToggleSelectAllSecondaryCategories,
      onExitSelectAllSecondaryCategories,
      onSecondaryCategoryPageChange,
      onSubmitActionChange,
      onClearAllRequest,
      onClearAllDismiss,
      onBulkRemoveDismiss,
      onGrantListRetry,
    },
  } = useReportAccessView()

  return (
    <>
      <Space size='large'>
        <ReportAccessHeaderCard
          selectedPrimaryCategory={selectedPrimaryCategory}
          isAllPrimaryCategories={isAllPrimaryCategories}
          onSelectPrimaryCategory={onSelectPrimaryCategory}
          onSelectAllPrimaryCategories={onSelectAllPrimaryCategories}
        />

        <Space direction='horizontal' align='start'>
          {/* Left pane unmounts entirely in ALL-PrimaryCategories mode
              (BHV-005) — the branch-less Angular fetch is intentionally
              skipped */}
          {showSecondaryCategoryPane && (
            <Space.Item width={1 / 3}>
              <Card>
                <Card.Content>
                  {selectedPrimaryCategory ? (
                    <ReportAccessSecondaryCategoryListPresenter
                      key={selectedPrimaryCategory.id}
                      primaryCategoryId={selectedPrimaryCategory.id}
                      primaryCategoryName={selectedPrimaryCategory.name}
                      page={secondaryCategoryPage}
                      selectedSecondaryCategoryId={
                        selectedSecondaryCategory?.id
                      }
                      isAllSecondaryCategoriesSelected={
                        isAllSecondaryCategories
                      }
                      onSelect={onSelectSecondaryCategory}
                      onToggleSelectAll={onToggleSelectAllSecondaryCategories}
                      onExitSelectAll={onExitSelectAllSecondaryCategories}
                      onPageChange={onSecondaryCategoryPageChange}
                    />
                  ) : (
                    <ReportAccessSectionLoader />
                  )}
                </Card.Content>
              </Card>
            </Space.Item>
          )}

          <Space.Item width={detailPaneWidth}>
            <Card>
              <Card.Content>
                {detailPaneState === ReportAccessDetailPaneStates.LOADING && (
                  <ReportAccessSectionLoader />
                )}

                {detailPaneState ===
                  ReportAccessDetailPaneStates.PRIMARY_CATEGORY_ERROR && (
                  <ErrorAlert>
                    {intl.formatMessage(messages.primaryCategoryErrorMessage)}
                  </ErrorAlert>
                )}

                {detailPaneState ===
                  ReportAccessDetailPaneStates.NO_PRIMARY_CATEGORIES && (
                  <ReportAccessDetailEmptyState paneState={detailPaneState} />
                )}

                {detailPaneState ===
                  ReportAccessDetailPaneStates.NO_SECONDARY_CATEGORIES && (
                  <ReportAccessDetailEmptyState paneState={detailPaneState} />
                )}

                {detailPaneState ===
                  ReportAccessDetailPaneStates.SECONDARY_CATEGORIES_ERROR && (
                  <ReportAccessDetailEmptyState paneState={detailPaneState} />
                )}

                {detailPaneState ===
                  ReportAccessDetailPaneStates.NO_SECONDARY_CATEGORY && (
                  <ReportAccessDetailEmptyState paneState={detailPaneState} />
                )}

                {detailPaneState === ReportAccessDetailPaneStates.READY && (
                  <Space>
                    <ReportAccessDetailHeader
                      mode={detailMode}
                      secondaryCategoryName={selectedSecondaryCategory?.name}
                      primaryCategoryName={selectedPrimaryCategory?.name}
                    />

                    <ReportAccessActionRow
                      isAllPrimaryCategories={isAllPrimaryCategories}
                      submitAction={submitAction}
                      isRemoveAction={isRemoveAction}
                      onSubmitActionChange={onSubmitActionChange}
                      onSubmit={onSubmitUser}
                      isSubmitting={isSubmitting}
                      isSubmitDisabled={isSubmitDisabled}
                      userPickerFocusRequest={userPickerFocusRequest}
                    />

                    {grantListSecondaryCategoryId !== undefined && (
                      <ReportAccessGrantListPresenter
                        secondaryCategoryId={grantListSecondaryCategoryId}
                        highlightedGrantId={highlightedGrantId}
                        isGrantCommandPending={isGrantCommandPending}
                        onRemoveGrant={onRemoveGrant}
                        onClearAll={onClearAllRequest}
                        onRetry={onGrantListRetry}
                      />
                    )}
                  </Space>
                )}
              </Card.Content>
            </Card>
          </Space.Item>
        </Space>
      </Space>

      {/* FS-owned danger confirmation: clear all grants on one
          SecondaryCategory (BHV-019) */}
      <ConfirmationModal
        open={isClearAllConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.clearAllConfirmHeader)}
        content={intl.formatMessage(messages.clearAllConfirmMessage, {
          count: pendingClearAllCount,
          secondaryCategoryName: selectedSecondaryCategory?.name,
        })}
        confirmButtonText={intl.formatMessage(messages.removeConfirmButtonText)}
        cancelButtonText={intl.formatMessage(messages.cancelButtonText)}
        onConfirm={onClearAllConfirm}
        handleDismiss={onClearAllDismiss}
      />

      {/* FS-owned danger confirmation: bulk remove from all
          PrimaryCategories (BHV-025) */}
      <ConfirmationModal
        open={isBulkRemoveConfirmOpen}
        type='danger'
        header={intl.formatMessage(messages.bulkRemoveConfirmHeader)}
        content={intl.formatMessage(messages.bulkRemoveConfirmMessage, {
          userFullName: pendingBulkRemoveUser?.fullName,
        })}
        confirmButtonText={intl.formatMessage(messages.removeConfirmButtonText)}
        cancelButtonText={intl.formatMessage(messages.cancelButtonText)}
        onConfirm={onBulkRemoveConfirm}
        handleDismiss={onBulkRemoveDismiss}
      />
    </>
  )
}

ReportAccessView.displayName = 'ReportAccessView'
